/**
 * Reads the impersonation token, checking the URL search params first and
 * falling back to `sessionStorage` so the session survives page refreshes.
 *
 * Resolution order:
 * 1. If `impersonation_token` is present in the URL, it is stored in
 *    `sessionStorage`, removed from the address bar (so the JWT is not
 *    visible in browser history or shared URLs), and returned.
 * 2. Otherwise, any token previously persisted in `sessionStorage` is returned.
 * 3. Returns `null` when no token is found in either source.
 *
 * Intended to be used as a lazy `useState` initializer in App so the token is
 * available on the very first render — before any router guard evaluates auth
 * state. Reading it inside a `useEffect` would create a race condition where
 * Firebase's `onAuthStateChanged` listener fires (sees no user → unauthenticated)
 * before the token is ever passed to `HetivoAuthProvider`.
 */
const IMPERSONATION_TOKEN_KEY = 'impersonation_token';

export function readImpersonationTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get(IMPERSONATION_TOKEN_KEY);

  if (token) {
    const url = new URL(window.location.href);
    url.searchParams.delete(IMPERSONATION_TOKEN_KEY);
    window.history.replaceState({}, '', url.toString());
    sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
    return token;
  }

  return sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
}
